<template>
  <iColumn gap="extraLarge" width="fill">
    <iColumn
      align="center"
      gap="small"
      width="fill"
      :style-overrides="identityStyleOverrides"
    >
      <AvatarImage :src="userAvatar" size="84px" />
      <iText
        font-size="large"
        font-weight="dark"
        :wrap="false"
        :overflow="true"
      >
        {{ fullName }}
      </iText>
      <iUploader @upload-file="uploadAvatar">
        <iButton
          variant="text"
          size="small"
          :style-overrides="accentLinkStyleOverrides"
        >
          Change photo
        </iButton>
      </iUploader>
    </iColumn>

    <iColumn v-if="isEmailBeingChanged" wrap="nowrap" width="fill">
      <iText>
        You still need to finalize your change of email address
      </iText>
      <iParagraph>
        inspiration will not send any emails to your new address until you complete the process.
        please check your email for a message from account@activate.social with the subject:
        "Change your Email Address" and follow the instructions.
        If you having trouble finding this email, try resending, or contact support.
      </iParagraph>
      <iText>
        Old Email: <b>{{ currentEmail }}</b>
      </iText>
      <iText>
        New Email: <b>{{ requestedEmailAddress }}</b>
      </iText>
      <iButton variant="tertiary" @click="submitCancelEmailChange">
        Cancel Email Change Process
      </iButton>
    </iColumn>

    <iColumn gap="small" width="fill">
      <iText
        font-size="verySmall"
        text-case="uppercase"
        variant="subtle"
        font-weight="dark"
      >
        Account
      </iText>
      <iCard width="fill" :style-overrides="groupCardStyleOverrides">
        <template #body>
          <iColumn gap="none" width="fill">
            <iColumn
              v-for="(field, index) in accountFields"
              :key="field.key"
              gap="none"
              width="fill"
            >
              <iRow
                align="between"
                vertical-align="middle"
                width="fill"
                wrap="nowrap"
                gap="standard"
                cursor="pointer"
                :style-overrides="interactiveRowStyleOverrides"
                @click="toggleField(field.key)"
              >
                <iText :wrap="false" :overflow="true">
                  {{ field.label }}
                </iText>
                <iRow
                  gap="small"
                  vertical-align="middle"
                  width="hug"
                  wrap="nowrap"
                >
                  <iText variant="subtle" :wrap="false" :overflow="true">
                    {{ field.value }}
                  </iText>
                  <iIcon
                    :icon="openField === field.key ? 'caret-down' : 'caret-right'"
                    size="small"
                    variant="subtle"
                  />
                </iRow>
              </iRow>
              <iColumn
                v-if="openField === field.key"
                gap="extraSmall"
                width="fill"
                :style-overrides="editPanelStyleOverrides"
              >
                <iText variant="subtle" font-size="small">
                  {{ field.editLabel }}
                </iText>
                <iTextEditInput
                  ref="fieldEditor"
                  :model-value="field.value"
                  :is-loading="field.loading"
                  save-action-style="icon"
                  cancel-action-style="none"
                  display-actions-as-block
                  width="fill"
                  @submit="saveField"
                />
              </iColumn>
              <iDivider v-if="index < accountFields.length - 1" color="light" />
            </iColumn>
          </iColumn>
        </template>
      </iCard>
    </iColumn>

    <iText v-if="changeEmailError" variant="error">
      {{ changeEmailError }}
    </iText>

    <iColumn gap="small" width="fill">
      <iText
        font-size="verySmall"
        text-case="uppercase"
        variant="subtle"
        font-weight="dark"
      >
        Security
      </iText>
      <iCard width="fill" :style-overrides="groupCardStyleOverrides">
        <template #body>
          <iColumn gap="none" width="fill">
            <iRow
              align="between"
              vertical-align="middle"
              width="fill"
              wrap="nowrap"
              gap="standard"
              cursor="pointer"
              :style-overrides="interactiveRowStyleOverrides"
              @click="showPasswordModal"
            >
              <iText :wrap="false" :overflow="true">
                Password
              </iText>
              <iRow
                gap="small"
                vertical-align="middle"
                width="hug"
                wrap="nowrap"
              >
                <iText variant="branded" :wrap="false" :overflow="true">
                  Change
                </iText>
                <iIcon :icon="'caret-right'" size="small" variant="subtle" />
              </iRow>
            </iRow>
          </iColumn>
        </template>
      </iCard>
    </iColumn>

    <iColumn gap="small" width="fill">
      <iText
        font-size="verySmall"
        text-case="uppercase"
        variant="subtle"
        font-weight="dark"
      >
        Appearance
      </iText>
      <iCard width="fill" :style-overrides="groupCardStyleOverrides">
        <template #body>
          <iColumn gap="none" width="fill">
            <iRow
              align="between"
              vertical-align="middle"
              width="fill"
              wrap="nowrap"
              gap="standard"
              :style-overrides="rowStyleOverrides"
            >
              <iText :wrap="false" :overflow="true">
                Theme
              </iText>
              <iRow width="hug" vertical-align="middle" wrap="nowrap">
                <iRadioBar
                  :model-value="currentThemeMode"
                  :options="themeModeOptions"
                  :style-overrides="themeBarStyleOverrides"
                  width="hug"
                  @update:model-value="toggleThemeMode"
                />
              </iRow>
            </iRow>
          </iColumn>
        </template>
      </iCard>
    </iColumn>

    <iRow align="center" width="fill">
      <iButton
        variant="text"
        size="small"
        :style-overrides="destructiveLinkStyleOverrides"
        data-test="account-delete-open"
        @click="openDeleteAccountModal"
      >
        Delete account
      </iButton>
    </iRow>
  </iColumn>
  <iModal
    v-if="deleteAccountModalVisible"
    title="Delete account"
    disable-enter
    :max-width="480"
    :focus-first-element="false"
    primary-action-button-label="Delete account"
    :primary-action-button-loading="isDeletingAccount"
    :primary-action-button-disabled="!deleteAccountPassword"
    :secondary-action-button-disabled="isDeletingAccount"
    @close="closeDeleteAccountModal"
    @click:secondary="closeDeleteAccountModal"
    @click:primary="confirmDeleteAccount"
  >
    <template #body>
      <iColumn gap="large" width="fill">
        <iParagraph>
          Deleting your account signs you out and you will not be able to sign back in.
        </iParagraph>
        <iTextInput
          v-model="deleteAccountPassword"
          label="Confirm your password"
          type="password"
          width="fill"
          data-test="account-delete-password"
        />
        <iText v-if="deleteAccountErrorMessage" variant="error" data-test="account-delete-error">
          {{ deleteAccountErrorMessage }}
        </iText>
      </iColumn>
    </template>
  </iModal>
  <iModal
    v-if="changePasswordModalVisible"
    title="Change Password"
    :max-width="600"
    :focus-first-element="false"
    @close="closePasswordModal"
    @click:primary="savePasswordChange"
  >
    <template #body>
      <iColumn gap="large" :max-width="600" width="fill">
        <iTextInput
          v-model="currentPassword"
          label="Current Password"
          type="password"
          width="fill"
        />

        <iTextInput
          v-model="newPassword"
          label="New Password"
          type="password"
          width="fill"
        />

        <PasswordComplexity
          :password="newPassword"
          @update:password="validatePasswordComplexity"
        />
      </iColumn>
      <iText v-if="userDetailsErrorMessage" variant="error">
        {{ userDetailsErrorMessage }}
      </iText>
      <iText v-if="successfulPasswordChange" variant="success">
        Your password has been changed
      </iText>
    </template>
  </iModal>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import PasswordComplexity from "@/components/PasswordComplexity";
import { useUserStore } from "@/stores/user-store";
import { SECTION_SETTINGS, PAGE_USER_ACCOUNT_SETTINGS } from "@/constants/nav-constants";
import { useGeneralStore } from "%/stores/general-store";
import AvatarImage from "%/components/AvatarImage";
import { useStyleStore } from "@bloglovin/vue-component-library";
import { useUserPreferenceStore } from "@/stores/user-preference-store";

export default {
  name: "UserAccountManagement",
  components: {
    AvatarImage,
    PasswordComplexity,
  },
  props: {
    appId: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  data() {
    return {
      openField: null,
      SECTION_SETTINGS,
      PAGE_USER_ACCOUNT_SETTINGS,
      currentPassword: "",
      newPassword: "",
      validNewPassword: false,
      changePasswordModalVisible: false,
      deleteAccountModalVisible: false,
      deleteAccountPassword: "",
    };
  },
  computed: {
    ...mapState(useUserStore, [
      "isLoading",
      "isEditingUserData",
      "loadingChangeFirstName",
      "loadingChangeLastName",
      "userDetailsErrorMessage",
      "changeEmailError",
      "successfulPasswordChange",
      "isDeletingAccount",
      "deleteAccountErrorMessage",
    ]),
    ...mapWritableState(useUserStore, [
      "editUserDetails",
      "userData",
      "emailData",
      "emailStatus",
    ]),
    ...mapState(useGeneralStore, [
      "userAvatar",
      "settingsBreadcrumbs",
    ]),
    themeModeOptions() {
      return [
        {
          value: "light-mode",
          label: "Light",
          tooltip: "",
        },
        {
          value: "dark-mode",
          label: "Dark",
          tooltip: "",
        },
        {
          value: "unset",
          label: "System",
          tooltip: "",
        },
      ];
    },
    isEmailConfirmed() {
      return this.emailStatus.isEmailConfirmed;
    },
    isEmailBeingChanged() {
      return this.emailStatus.isEmailBeingChanged;
    },
    currentEmail() {
      return this.emailData.email_address;
    },
    requestedEmailAddress() {
      return this.emailStatus.requestedNewEmail;
    },
    accountFields() {
      return [
        {
          key: "email",
          label: "Email",
          value: this.emailData.email_address,
          editLabel: "Enter new email",
          inputType: "email",
          loading: this.isLoading,
          save: this.saveEmailChange,
        },
        {
          key: "firstName",
          label: "First name",
          value: this.userData.first_name,
          editLabel: "Enter new first name",
          inputType: "text",
          loading: this.loadingChangeFirstName,
          save: this.submitNewFirstName,
        },
        {
          key: "lastName",
          label: "Last name",
          value: this.userData.last_name,
          editLabel: "Enter new last name",
          inputType: "text",
          loading: this.loadingChangeLastName,
          save: this.submitNewLastName,
        },
      ];
    },
    editPanelStyleOverrides() {
      return {
        paddingLeft: "large",
        paddingRight: "large",
        paddingBottom: "standard",
      };
    },
    destructiveLinkStyleOverrides() {
      return {
        textVariantFontColor: "error",
        textVariantHoverFontColor: "error",
      };
    },
    accentLinkStyleOverrides() {
      return {
        textVariantFontColor: "branded",
        textVariantHoverFontColor: "branded",
      };
    },
    groupCardStyleOverrides() {
      return {
        backgroundColor: "paper",
        borderWidth: "none",
        borderRadius: "12px",
        paddingTop: "none",
        paddingBottom: "none",
        paddingLeft: "none",
        paddingRight: "none",
      };
    },
    identityStyleOverrides() {
      return { paddingTop: "small", paddingBottom: "small" };
    },
    interactiveRowStyleOverrides() {
      return {
        ...this.rowStyleOverrides,
        hoverBackgroundColor: "secondary.standard",
      };
    },
    rowStyleOverrides() {
      return {
        paddingTop: "standard",
        paddingBottom: "standard",
        paddingLeft: "large",
        paddingRight: "large",
      };
    },
    fullName() {
      return `${this.userData.first_name ?? ""} ${this.userData.last_name ?? ""}`.trim();
    },
    themeBarStyleOverrides() {
      return {
        barBackgroundColor: "none",
        barBorderRadius: "large",
        barPadding: "extraSmall",
        itemPadding: "extraSmall",
        activeItemBackgroundColor: "secondary.standard",
        activeItemBorderColor: "standard",
        activeItemBorderRadius: "standard",
      };
    },
    currentThemeMode() {
      return useStyleStore().appThemeModes[this.appId] ?? "unset";
    },
  },
  beforeMount() {
    this.fetchEmailStatus();
  },
  methods: {
    ...mapActions(useUserStore, [
      "fetchEmailStatus",
      "cancelEmailChange",
      "submitNewEmail",
      "submitNewPassword",
      "submitNewFirstName",
      "submitNewLastName",
      "uploadAvatar",
      "deleteAccount",
    ]),
    fieldByKey(key) {
      const field = this.accountFields.find(candidate => candidate.key === key);
      if (!field) {
        throw new Error(`UserAccountManagement: unknown account field "${key}".`);
      }

      return field;
    },
    toggleField(key) {
      if (this.openField === key) {
        this.closeField();
        return;
      }

      this.openField = key;
      this.$nextTick(() => this.openEditorForField());
    },
    closeField() {
      this.openField = null;
    },
    openEditorForField() {
      const editors = this.$refs.fieldEditor;
      const editor = Array.isArray(editors) ? editors[0] : editors;
      if (!editor || typeof editor.startEditing !== "function") {
        throw new Error("UserAccountManagement: expanded field editor did not mount.");
      }

      editor.startEditing();
    },
    saveField(newValue) {
      if (!this.openField) {
        return;
      }

      const field = this.fieldByKey(this.openField);
      const trimmed = (newValue ?? "").trim();
      if (trimmed !== "" && trimmed !== (field.value ?? "")) {
        field.save(trimmed);
      }

      this.closeField();
    },
    toggleThemeMode(newThemeMode) {
      if (this.currentThemeMode === newThemeMode) {
        return;
      }
      const styleStore = useStyleStore();
      styleStore.setAppThemeMode(this.appId, newThemeMode, styleStore.DEFAULT_THEME_ID);
      // setAppThemeMode leaves the mapped-theme cache alone, so without this
      // the new mode only shows after a reload.
      styleStore.invalidateActualTheme(styleStore.DEFAULT_THEME_ID);
      useUserPreferenceStore().updateUserPreferenceByName("appThemeModes", styleStore.appThemeModes);
    },
    saveEmailChange(email) {
      this.submitNewEmail(email);
    },
    submitCancelEmailChange() {
      this.cancelEmailChange();
    },
    savePasswordChange() {
      this.submitNewPassword(this.currentPassword, this.newPassword);
    },
    updatePassword(value) {
      this.newPassword = value;
    },
    validatePasswordComplexity(value) {
      this.validNewPassword = value;
    },
    closePasswordModal() {
      this.changePasswordModalVisible = false;
      this.currentPassword = "";
      this.newPassword = "";
    },
    openDeleteAccountModal() {
      document.activeElement.blur();
      this.deleteAccountModalVisible = true;
    },
    closeDeleteAccountModal() {
      if (this.isDeletingAccount) {
        return;
      }
      this.deleteAccountModalVisible = false;
      this.deleteAccountPassword = "";
    },
    confirmDeleteAccount() {
      this.deleteAccount(this.deleteAccountPassword);
    },
    showPasswordModal() {
      document.activeElement.blur();
      this.changePasswordModalVisible = true;
    },
  },
};
</script>
